import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { leadsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { handleError } from '../lib/errorHandler';
import {
  Users, Search, Upload, ArrowRight, Loader2, Snowflake, Layers,
  Mail, Phone, Building2, ChevronLeft, ChevronRight, Sparkles, MessageCircle, Pencil, Trash2,
} from 'lucide-react';

const PAGE_SIZE = 50;

export default function Audience() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [batches, setBatches] = useState([]);
  const [activeBatch, setActiveBatch] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [promoting, setPromoting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      if (search) params.search = search;
      if (activeBatch) params.batch = activeBatch;
      const res = await leadsAPI.getAudience(params);
      const d = res.data || {};
      setLeads(d.leads || []);
      setTotal(d.total || 0);
      setBatches(d.batches || []);
    } catch (e) {
      handleError(e);
    } finally {
      setLoading(false);
    }
  }, [page, search, activeBatch]);

  useEffect(() => { load(); }, [load]);

  const handlePromote = async (lead) => {
    setPromoting(lead.id);
    try {
      await leadsAPI.promoteToPipeline(lead.id);
      toast.success(`${lead.name} moved to your pipeline`, { description: 'They now appear on the Lead Kanban.' });
      setLeads((prev) => prev.filter((l) => l.id !== lead.id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e) {
      handleError(e);
    } finally {
      setPromoting(null);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE) || 1;

  const handleRenameBatch = async () => {
    if (!activeBatch) return;
    const next = window.prompt('Rename this list to:', activeBatch);
    if (!next || !next.trim() || next.trim() === activeBatch) return;
    try {
      const res = await leadsAPI.renameBatch(activeBatch, next.trim());
      toast.success(`Renamed to "${next.trim()}"`, { description: `${res.data.renamed} leads updated` });
      setActiveBatch(next.trim());
      setPage(0);
      load();
    } catch (e) {
      handleError(e);
    }
  };

  const handleDeleteBatch = async () => {
    if (!activeBatch) return;
    if (!window.confirm(`Delete the "${activeBatch}" list? This permanently removes those cold leads. Leads already promoted to your pipeline are NOT affected.`)) return;
    try {
      const res = await leadsAPI.deleteBatch(activeBatch);
      toast.success(`Deleted "${activeBatch}"`, { description: `${res.data.deleted} leads removed` });
      setActiveBatch(null);
      setPage(0);
      load();
    } catch (e) {
      handleError(e);
    }
  };

  return (
    <div className="space-y-6" data-testid="audience-page">
      {/* Header + Pipeline/Audience toggle */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="audience-title">
            <Snowflake className="h-6 w-6 text-cyan-400" /> Audience
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Your cold, bulk-imported leads — kept off the pipeline Kanban so it stays clean.
            Target them with campaigns; anyone who replies is auto-promoted to your pipeline.
          </p>
        </div>
        <div className="inline-flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/leads?import=1')} data-testid="audience-import-btn">
            <Upload className="h-4 w-4 mr-2" /> Import CSV
          </Button>
          <div className="inline-flex rounded-lg border border-border overflow-hidden" data-testid="pipeline-audience-toggle">
          <button
            onClick={() => navigate('/leads')}
            className="px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted/50 transition-colors"
            data-testid="toggle-pipeline"
          >
            Pipeline
          </button>
          <button
            className="px-4 py-2 text-sm font-medium bg-cyan-500/15 text-cyan-300 border-l border-border"
            data-testid="toggle-audience"
          >
            Audience {total > 0 && <span className="ml-1 opacity-80">· {total.toLocaleString()}</span>}
          </button>
        </div>
        </div>
      </div>

      {/* Batch chips */}
      {batches.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="audience-batches">
          <span className="text-xs text-muted-foreground flex items-center gap-1"><Layers className="h-3.5 w-3.5" /> Lists:</span>
          <button
            onClick={() => { setActiveBatch(null); setPage(0); }}
            className={`px-3 py-1 rounded-full text-xs border transition-colors ${!activeBatch ? 'bg-primary/15 border-primary/40 text-primary' : 'border-border text-muted-foreground hover:bg-muted/40'}`}
            data-testid="batch-chip-all"
          >
            All ({total.toLocaleString()})
          </button>
          {batches.map((b) => (
            <button
              key={b.name}
              onClick={() => { setActiveBatch(b.name); setPage(0); }}
              className={`px-3 py-1 rounded-full text-xs border transition-colors ${activeBatch === b.name ? 'bg-primary/15 border-primary/40 text-primary' : 'border-border text-muted-foreground hover:bg-muted/40'}`}
              data-testid={`batch-chip-${b.name}`}
            >
              {b.name} ({b.count.toLocaleString()})
            </button>
          ))}
          {activeBatch && (
            <div className="flex items-center gap-1 ml-1 pl-2 border-l border-border">
              <button
                onClick={handleRenameBatch}
                className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                title="Rename list"
                data-testid="batch-rename-btn"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={handleDeleteBatch}
                className="p-1.5 rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors"
                title="Delete list"
                data-testid="batch-delete-btn"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="Search name, company, email, phone…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          data-testid="audience-search"
        />
      </div>

      {/* Table / states */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-16 text-muted-foreground" data-testid="audience-loading">
              <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin" />
              Loading audience…
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-16" data-testid="audience-empty">
              <div className="inline-flex p-3 rounded-full bg-cyan-500/10 mb-3">
                <Snowflake className="h-8 w-8 text-cyan-400" />
              </div>
              <p className="font-medium">Your audience is empty</p>
              <p className="text-sm text-muted-foreground mt-1 mb-4 max-w-md mx-auto">
                Bulk-import a CSV of cold leads. They land here (not on your Kanban) so you can
                target them with mail, WhatsApp or voice campaigns.
              </p>
              <Button onClick={() => navigate('/leads?import=1')} data-testid="audience-import-cta">
                <Upload className="h-4 w-4 mr-2" /> Import leads
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-border/50" data-testid="audience-list">
              {leads.map((lead) => {
                const icp = lead.ai_score ?? lead.fit_score;
                return (
                  <div key={lead.id} className="flex items-center justify-between px-4 py-3 hover:bg-muted/10 transition-colors" data-testid={`audience-row-${lead.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <button onClick={() => navigate(`/leads/${lead.id}`)} className="font-medium truncate hover:underline text-left" data-testid={`audience-name-${lead.id}`}>
                          {lead.name || 'Unnamed'}
                        </button>
                        {typeof icp === 'number' && (
                          <Badge variant="outline" className="text-xs shrink-0">ICP {icp}</Badge>
                        )}
                        {lead.import_batch && (
                          <Badge variant="outline" className="text-xs shrink-0 text-cyan-300 border-cyan-500/30">{lead.import_batch}</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground mt-0.5 flex-wrap">
                        {lead.company && <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{lead.company}</span>}
                        {lead.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{lead.email}</span>}
                        {lead.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{lead.phone}</span>}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0 ml-3"
                      disabled={promoting === lead.id}
                      onClick={() => handlePromote(lead)}
                      data-testid={`promote-btn-${lead.id}`}
                    >
                      {promoting === lead.id ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-1" />}
                      To pipeline
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between" data-testid="audience-pagination">
          <span className="text-sm text-muted-foreground">Page {page + 1} of {totalPages} · {total.toLocaleString()} leads</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="audience-prev">
              <ChevronLeft className="h-4 w-4" /> Prev
            </Button>
            <Button size="sm" variant="outline" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="audience-next">
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Feed-to-campaign — launch a mail/WhatsApp/voice campaign for this audience list */}
      {leads.length > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap p-4 rounded-lg border border-primary/20 bg-primary/5" data-testid="audience-campaign-hint">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Sparkles className="h-4 w-4 text-amber-400" />
            Ready to reach {activeBatch ? `the "${activeBatch}" list` : 'this audience'}? Launch a campaign — pick a channel:
          </div>
          <div className="flex gap-2">
            {[
              { ch: 'email', label: 'Email', Icon: Mail },
              { ch: 'whatsapp', label: 'WhatsApp', Icon: MessageCircle },
              { ch: 'voice', label: 'Voice', Icon: Phone },
            ].map(({ ch, label, Icon }) => (
              <Button
                key={ch}
                variant={ch === 'email' ? 'default' : 'outline'}
                onClick={() => navigate(`/campaigns?open=1&channel=${ch}${activeBatch ? `&batch=${encodeURIComponent(activeBatch)}` : ''}`)}
                data-testid={`audience-launch-${ch}`}
              >
                <Icon className="h-4 w-4 mr-2" /> {label}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
